import { ArrowRight, Dog, Cat, Bird, PawPrint } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DashboardBookingStatus, DashboardUpcomingBooking } from "./types";

export interface DashboardBookingListProps {
  bookings: DashboardUpcomingBooking[];
  title: string;
  viewAllLabel: string;
  emptyLabel: string;
  onBookingClick?: (booking: DashboardUpcomingBooking) => void;
  onViewAll?: () => void;
  className?: string;
}

const STATUS_STYLES: Record<DashboardBookingStatus, string> = {
  confirmed: "bg-biz-success-subtle text-biz-success border-biz-success-border",
  pending: "bg-biz-warning-subtle text-biz-warning-foreground border-biz-warning-border",
  checked_in: "bg-biz-info-subtle text-biz-info border-biz-info-border",
  in_progress: "bg-biz-primary-subtle text-biz-primary-subtle-fg border-biz-primary/30",
  completed: "bg-biz-muted text-biz-foreground-muted border-biz-border",
};

function speciesIcon(species: string) {
  const s = species.toLowerCase();
  if (s.includes("dog") || s === "köpek") return Dog;
  if (s.includes("cat") || s === "kedi") return Cat;
  if (s.includes("bird") || s === "kuş") return Bird;
  return PawPrint;
}

export function DashboardBookingList({
  bookings,
  title,
  viewAllLabel,
  emptyLabel,
  onBookingClick,
  onViewAll,
  className,
}: DashboardBookingListProps) {
  return (
    <section
      className={cn(
        "flex flex-col rounded-biz-card border border-biz-border bg-biz-surface-elevated biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-bookings-title"
    >
      <header className="flex items-center justify-between border-b border-biz-border px-4 py-3">
        <h3 id="dash-bookings-title" className="text-sm font-semibold text-biz-foreground">
          {title}
        </h3>
        {onViewAll && bookings.length > 0 && (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1 text-xs font-semibold text-biz-primary-subtle-fg hover:underline biz-focus-ring rounded"
          >
            {viewAllLabel}
            <ArrowRight className="h-3 w-3" weight="bold" aria-hidden />
          </button>
        )}
      </header>
      {bookings.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-biz-foreground-muted">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-biz-border" role="list">
          {bookings.map((b) => {
            const Icon = speciesIcon(b.petSpecies);
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => onBookingClick?.(b)}
                  className="flex w-full min-h-[60px] items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-biz-muted/50 biz-focus-ring"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-biz-btn bg-biz-primary-subtle text-biz-primary-subtle-fg">
                    <Icon className="h-5 w-5" weight="fill" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5 truncate">
                      <span className="truncate text-sm font-semibold text-biz-foreground">
                        {b.customerName}
                      </span>
                      <span className="truncate text-xs text-biz-foreground-muted">· {b.petName}</span>
                    </div>
                    <div className="truncate text-xs text-biz-foreground-muted">
                      {b.serviceName} · <span className="font-mono-num">{b.whenDisplay}</span>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                      STATUS_STYLES[b.status],
                    )}
                  >
                    {b.statusLabel}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
